import * as React from 'react';

import { CalloutPriority } from '../../../../__generated__/globalTypes';
import { TrendUpIcon } from '../../../../icons';
import { formatWeeksAgo, toCurrency } from '../../../../utils/formatting';
import BankAccountNameAndAvatar from '../../../BankAccountNameAndAvatar';
import Card from '../../../base/Card';
import MerchantNameAndAvatar from '../../../MerchantNameAndAvatar';
import CalloutDismissConfirmation from '../CalloutDismissConfirmation';

import type { Callout } from '../../CalloutCard';

export interface IUnusualCumulativeSpendMetadata {
  subtype: 'global' | 'account' | 'merchant';
  period: 'weekly';
  totalSpend: number;
  percentile: number;
  percentChangeFromMean: number;
  historicalMean: number;
}

interface IUnusualCumulativeSpendCalloutCardProps {
  callout: Callout;
  /**
   * Called when the user dismisses the callout.
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismiss?: (appropriate?: boolean) => void;
  className?: string;
  onClick?: () => void;
  /**
   * 'full' shows full details (entity, amount), used in carousel.
   * 'compact' shows description only, used in slideout.
   * @default 'full'
   */
  variant?: 'full' | 'compact';
}

export function isUnusualCumulativeSpendMetadata(metadata: unknown): metadata is IUnusualCumulativeSpendMetadata {
  if (
    typeof metadata !== 'object' ||
    metadata === null ||
    !('subtype' in metadata) ||
    !('period' in metadata) ||
    !('totalSpend' in metadata) ||
    !('percentile' in metadata) ||
    !('percentChangeFromMean' in metadata) ||
    !('historicalMean' in metadata)
  ) {
    return false;
  }

  const { subtype, period, totalSpend, percentile, percentChangeFromMean, historicalMean } =
    metadata as IUnusualCumulativeSpendMetadata;

  if (subtype !== 'global' && subtype !== 'account' && subtype !== 'merchant') {
    return false;
  }

  if (period !== 'weekly') {
    return false;
  }

  if (
    typeof totalSpend !== 'number' ||
    typeof percentile !== 'number' ||
    typeof percentChangeFromMean !== 'number' ||
    typeof historicalMean !== 'number'
  ) {
    return false;
  }

  return true;
}

const bold = (text: string) => <span className="text-primary font-bold">{text}</span>;

function formatSpendComparison(percentChangeFromMean: number): React.ReactNode {
  const percentAbove = Math.round(percentChangeFromMean);

  if (percentAbove >= 300) {
    return <>more than {bold(`${Math.round(percentAbove / 100 + 1)}x`)} the usual</>;
  }
  if (percentAbove >= 200) {
    return <>more than {bold('triple')} the usual</>;
  }
  if (percentAbove >= 100) {
    return <>more than {bold('double')} the usual</>;
  }
  return <>{bold(`${percentAbove}%`)} higher than usual</>;
}

function getDescription(metadata: IUnusualCumulativeSpendMetadata, entityName?: string): React.ReactNode {
  const comparison = formatSpendComparison(metadata.percentChangeFromMean);

  const entity = entityName ? (
    <span className="inline-block max-w-[14rem] truncate align-bottom">{entityName}</span>
  ) : undefined;

  if (metadata.subtype === 'merchant' && entity) {
    return (
      <>
        Spending at {entity} this week is {comparison}.
      </>
    );
  }
  if (metadata.subtype === 'account' && entity) {
    return (
      <>
        Spending from {entity} this week is {comparison}.
      </>
    );
  }
  return <>Overall spending this week is {comparison}.</>;
}

function getPeriodLabel(eventDate?: string): string {
  if (!eventDate) {
    return 'This week';
  }
  return formatWeeksAgo(eventDate);
}

const UnusualCumulativeSpendCalloutCard = ({
  callout,
  onDismiss,
  className,
  onClick,
  variant = 'full',
}: IUnusualCumulativeSpendCalloutCardProps) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleInitiateDismiss = React.useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleConfirmDismiss = React.useCallback(
    (appropriate: boolean) => {
      onDismiss?.(appropriate);
    },
    [onDismiss]
  );

  // Validate and extract metadata
  if (!isUnusualCumulativeSpendMetadata(callout.metadata)) {
    return null;
  }

  const metadata = callout.metadata;
  const isP0 = callout.priority === CalloutPriority.P0;

  // Determine entity name based on subtype
  let entityName: string | undefined;
  if (metadata.subtype === 'merchant' && callout.merchant) {
    entityName = callout.merchant.name;
  } else if (metadata.subtype === 'account' && callout.financialAccount) {
    entityName = callout.financialAccount.name;
  }

  const description = getDescription(metadata, entityName);
  const periodLabel = getPeriodLabel(callout.eventDate);

  // Confirmation state - show the confirmation UI
  if (showConfirmation) {
    return (
      <CalloutDismissConfirmation
        severity={isP0 ? 'error' : 'warning'}
        className={className}
        onConfirm={handleConfirmDismiss}
        headerIcon={<TrendUpIcon />}
        headerLabel="High spend"
        question="Did flagging this spending help you?"
      />
    );
  }

  // Normal state - show the callout card
  return (
    <Card severity={isP0 ? 'error' : 'warning'} onClick={onClick} className={className}>
      <Card.Header
        icon={<TrendUpIcon />}
        label="High spend"
        onDismiss={onDismiss ? handleInitiateDismiss : undefined}
      />
      {variant === 'full' ? (
        <Card.Body>
          {/* Row 1: Entity (avatar + name + details) | Amount + period */}
          <div className="flex items-start justify-between">
            {metadata.subtype === 'merchant' && callout.merchant ? (
              <MerchantNameAndAvatar
                displayNameClassName="font-semibold"
                merchant={callout.merchant}
                fallbackDescription={callout.merchant.name}
              />
            ) : metadata.subtype === 'account' && callout.financialAccount ? (
              <BankAccountNameAndAvatar
                financialAccount={callout.financialAccount}
                displayNameClassName="font-semibold"
              />
            ) : (
              <span className="text-primary text-md font-semibold">All spending</span>
            )}
            <div className="ml-2 flex shrink-0 flex-col items-end">
              <span className="text-error-primary text-md font-semibold">{toCurrency(metadata.totalSpend / 100)}</span>
              <span className="text-tertiary text-sm font-medium">{periodLabel}</span>
            </div>
          </div>
          {/* Row 2: Description */}
          <div className="flex flex-1 items-center">
            <Card.Text className="line-clamp-3">{description}</Card.Text>
          </div>
        </Card.Body>
      ) : (
        <Card.Body className="py-3">
          <Card.Text>{description}</Card.Text>
        </Card.Body>
      )}
    </Card>
  );
};

export default UnusualCumulativeSpendCalloutCard;
